import {
  createContext,
  useContext,
  useState,
  useMemo,
  type ReactNode,
} from 'react';

export type ColoredBlob = {
  color: [number, number, number, number]; // RGBA 0-1
  blob: Blob;
  isGhost?: boolean; // True for % modifier - preview only, exclude from export
  excludedFromExport?: boolean; // True for exclude_from_export() - show in preview (toggleable), exclude from export
};

type BlobContextType = {
  blob: Blob | null;
  setBlob: (blob: Blob | null) => void;
  coloredBlobs: ColoredBlob[];
  setColoredBlobs: (blobs: ColoredBlob[]) => void;
};

export const BlobContext = createContext<BlobContextType>({
  blob: null,
  setBlob: () => {},
  coloredBlobs: [],
  setColoredBlobs: () => {},
});

/**
 * Provider component with memoized value.
 *
 * Same fix as ColorProvider: prevents cascading re-renders of the entire
 * subtree when a parent re-renders for an unrelated reason.
 */
export function BlobProvider({ children }: { children: ReactNode }) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [coloredBlobs, setColoredBlobs] = useState<ColoredBlob[]>([]);

  const value = useMemo(
    () => ({ blob, setBlob, coloredBlobs, setColoredBlobs }),
    [blob, coloredBlobs], // setters from useState are stable
  );

  return <BlobContext.Provider value={value}>{children}</BlobContext.Provider>;
}

export const useBlob = () => {
  const context = useContext(BlobContext);
  if (!context) {
    throw new Error('useBlob must be used within a BlobProvider');
  }
  return context;
};
